import React, { useMemo } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { useSearch } from '@/hooks/useSearch'
import { PageHeader } from '@/components/ui/PageHeader'

interface SearchResult {
  type: 'user' | 'company' | 'project'
  id: string
  name: string
  displayName?: string
  email?: string
  department?: string
  domain?: string
  status?: string
}

const SearchResults: React.FC = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const query = searchParams.get('q') || ''
  const { results, isLoading } = useSearch(query)

  const grouped = useMemo(() => {
    // WI #323: `results.users[i]` is a search hit, not a full `User`. It
    // carries `name`, not `displayName` — reading the latter is what made
    // every user result render with a blank title.
    const users: SearchResult[] = (results?.users ?? []).map((u) => ({
      type: 'user' as const,
      id: u.id,
      name: u.name,
      displayName: u.name,
      email: u.email,
      department: u.department,
    }))
    const companies: SearchResult[] = (results?.companies ?? []).map((c) => ({
      type: 'company' as const,
      id: c.id,
      name: c.name,
      domain: c.domain,
    }))
    const projects: SearchResult[] = (results?.projects ?? []).map((p) => ({
      type: 'project' as const,
      id: p.id,
      name: p.name,
    }))
    return { users, companies, projects }
  }, [results])

  const handleResultClick = (result: SearchResult) => {
    switch (result.type) {
      case 'user':
        navigate(`/users/${result.id}`)
        break
      case 'company':
        navigate(`/companies/${result.id}`)
        break
      case 'project':
        navigate(`/projects/${result.id}`)
        break
    }
  }

  const ResultSection: React.FC<{
    title: string
    type: 'user' | 'company' | 'project'
    items: SearchResult[]
  }> = ({ title, type, items }) => {
    if (items.length === 0) return null

    return (
      <div style={{ marginBottom: '32px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
          <h2 style={{ fontSize: '16px', fontWeight: 600, margin: 0, color: 'var(--color-black)' }}>
            {title}
          </h2>
          <span
            style={{
              backgroundColor: 'var(--color-light-grey)',
              color: 'var(--color-dark-grey)',
              borderRadius: '12px',
              padding: '2px 8px',
              fontSize: '12px',
              fontWeight: 600,
            }}
          >
            {items.length}
          </span>
        </div>

        <div style={{ display: 'grid', gap: '8px' }}>
          {items.map((item) => (
            <div
              key={`${item.type}-${item.id}`}
              onClick={() => handleResultClick(item)}
              style={{
                padding: '16px',
                border: '1px solid var(--color-light-grey)',
                borderRadius: '8px',
                backgroundColor: 'var(--color-white)',
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.08)'
                e.currentTarget.style.borderColor = 'var(--color-magenta)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = 'none'
                e.currentTarget.style.borderColor = 'var(--color-light-grey)'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, color: 'var(--color-black)', marginBottom: '4px', fontSize: '14px' }}>
                    {item.displayName || item.name}
                  </div>
                  <div style={{ display: 'flex', gap: '16px', fontSize: '13px', color: 'var(--color-dark-grey)' }}>
                    {type === 'user' && (
                      <>
                        {item.email && <span>{item.email}</span>}
                        {item.department && <span>{item.department}</span>}
                      </>
                    )}
                    {type === 'company' && (
                      <>
                        {item.domain && <span>{item.domain}</span>}
                        {item.status && <span>{item.status}</span>}
                      </>
                    )}
                    {type === 'project' && (
                      <>
                        {item.status && <span>{item.status}</span>}
                      </>
                    )}
                  </div>
                </div>
                <div
                  style={{
                    fontSize: '12px',
                    fontWeight: 600,
                    color: 'var(--color-dark-grey)',
                    backgroundColor: 'var(--color-light-grey)',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    flexShrink: 0,
                    textTransform: 'capitalize',
                  }}
                >
                  {type}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div style={{ padding: '0' }}>
      <PageHeader title={`Search results ${query ? `for "${query}"` : ''}`} />

      <div style={{ padding: '24px' }}>
        {isLoading ? (
          <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--color-dark-grey)' }}>
            Searching...
          </div>
        ) : grouped.users.length + grouped.companies.length + grouped.projects.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <p style={{ fontSize: '16px', color: 'var(--color-dark-grey)', margin: 0 }}>
              No results found
            </p>
            {query && (
              <p style={{ fontSize: '14px', color: 'var(--color-dark-grey)', margin: '8px 0 0 0' }}>
                Try different search terms.
              </p>
            )}
          </div>
        ) : (
          <>
            <ResultSection title="Users" type="user" items={grouped.users} />
            <ResultSection title="Companies" type="company" items={grouped.companies} />
            <ResultSection title="Projects" type="project" items={grouped.projects} />
          </>
        )}
      </div>
    </div>
  )
}

export default SearchResults
